import React from "react";
import ReactDOM from "react-dom";
import { registerConfirmInstance } from "../../utils/confirm";

/**
 * ConfirmDialogProvider - Portal 기반 imperative confirm 모달.
 *
 * 사용:
 *   import { Confirm } from "utils/confirm";
 *   const ok = await Confirm.show({ title, message, confirmText, cancelText, variant });
 *
 * 동작:
 * - ESC, backdrop 클릭, cancel 버튼 → resolve(false)
 * - confirm 버튼 → resolve(true)
 * - unmount 시 pending Promise resolve(false)
 * - 동시 다중 호출: 기존 dialog는 resolve(false), 마지막 것만 표시
 */
class ConfirmDialogProvider extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            open: false,
            options: null,
        };
        this.resolver = null;
        this.confirmBtnRef = React.createRef();
        this.cancelBtnRef = React.createRef();
    }

    componentDidMount() {
        registerConfirmInstance(this);
        document.addEventListener("keydown", this.handleKeyDown);
    }

    componentDidUpdate(_prevProps, prevState) {
        // 모달이 열릴 때 confirm 버튼 포커스 (간단 포커스 트랩)
        if (this.state.open && !prevState.open && this.confirmBtnRef.current) {
            this.confirmBtnRef.current.focus();
        }
    }

    componentWillUnmount() {
        registerConfirmInstance(null);
        document.removeEventListener("keydown", this.handleKeyDown);
        // pending Promise 안전 종료
        if (this.resolver) {
            this.resolver(false);
            this.resolver = null;
        }
    }

    handleKeyDown = (e) => {
        if (!this.state.open) return;
        if (e.key === "Escape") {
            e.stopPropagation();
            this.resolve(false);
            return;
        }
        if (e.key === "Tab") {
            // 포커스 트랩: cancelBtn(first) ↔ confirmBtn(last) 2개 focusable 순환
            const cancelBtn = this.cancelBtnRef.current;
            const confirmBtn = this.confirmBtnRef.current;
            if (!cancelBtn || !confirmBtn) return;
            const active = document.activeElement;
            const insideDialog = active === cancelBtn || active === confirmBtn;
            if (e.shiftKey) {
                if (!insideDialog || active === cancelBtn) {
                    e.preventDefault();
                    confirmBtn.focus();
                }
            } else {
                if (!insideDialog || active === confirmBtn) {
                    e.preventDefault();
                    cancelBtn.focus();
                }
            }
        }
    };

    open = (options) => {
        // 이전 호출 pending 시 false로 종료
        if (this.resolver) {
            this.resolver(false);
            this.resolver = null;
        }
        return new Promise((resolve) => {
            this.resolver = resolve;
            this.setState({ open: true, options });
        });
    };

    resolve = (value) => {
        const r = this.resolver;
        this.resolver = null;
        this.setState({ open: false, options: null }, () => {
            if (r) r(value);
        });
    };

    handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            this.resolve(false);
        }
    };

    render() {
        const { open, options } = this.state;

        const modal = open && options ? (
            <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-overlay px-4 animate-fade-in"
                onClick={this.handleBackdropClick}
                role="presentation"
            >
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={options.title ? "confirm-dialog-title" : undefined}
                    aria-describedby="confirm-dialog-message"
                    className="w-full max-w-sm bg-surface-card border border-surface-border rounded-2xl shadow-elevated animate-scale-in"
                >
                    <div className="px-6 pt-6 pb-4">
                        {options.title && (
                            <h2
                                id="confirm-dialog-title"
                                className="text-content text-base font-semibold mb-2"
                            >
                                {options.title}
                            </h2>
                        )}
                        <p
                            id="confirm-dialog-message"
                            className="text-content-secondary text-sm leading-relaxed whitespace-pre-line"
                        >
                            {options.message}
                        </p>
                    </div>
                    <div className="flex gap-2 px-6 pb-6">
                        <button
                            ref={this.cancelBtnRef}
                            type="button"
                            onClick={() => this.resolve(false)}
                            className="flex-1 bg-surface-elevated text-content-secondary hover:bg-surface-hover transition-colors rounded-lg px-4 py-2.5 text-sm font-medium min-h-[44px]"
                        >
                            {options.cancelText || "취소"}
                        </button>
                        <button
                            ref={this.confirmBtnRef}
                            type="button"
                            onClick={() => this.resolve(true)}
                            className={`flex-1 transition-colors rounded-lg px-4 py-2.5 text-sm font-medium min-h-[44px] text-content-inverse ${
                                options.variant === "danger"
                                    ? "bg-loss hover:bg-loss-dark"
                                    : "bg-brand hover:bg-brand-light"
                            }`}
                        >
                            {options.confirmText || "확인"}
                        </button>
                    </div>
                </div>
            </div>
        ) : null;

        return (
            <>
                {this.props.children}
                {modal && typeof document !== "undefined"
                    ? ReactDOM.createPortal(modal, document.body)
                    : null}
            </>
        );
    }
}

export default ConfirmDialogProvider;
